/**
 * HistoryView Component
 * Displays user's Spotify listening history with pagination and selection
 */

import { useEffect, useMemo } from 'react';
import { Music, AlertTriangle } from 'lucide-react';
import { useRecentlyPlayed } from '@/api/hooks';
import { useHistoryStore } from '@/stores/history';
import { Skeleton } from '@/components/shared/Skeleton';
import { TrackTable } from '@/components/workbench/TrackTable';
import { HistoryHeader } from './HistoryHeader';
import { HistoryToolbar } from './HistoryToolbar';
import { HistoryTimeline } from './HistoryTimeline';

/**
 * Main History View component
 * Fetches recently played tracks and displays them with header and toolbar
 */
export function HistoryView() {
  const { data, isLoading, error, refetch } = useRecentlyPlayed({ limit: 50 });
  const { setCurrentPage, clearSelection, currentPage, selectedDate, setSelectedDate } = useHistoryStore();

  // Sync API data to history store when data changes
  useEffect(() => {
    if (data) {
      setCurrentPage({
        items: data.items,
        cursors: {
          before: data.cursors.before || undefined,
          after: data.cursors.after || undefined,
        },
      });
    }
  }, [data, setCurrentPage]);

  // Clear selection when component unmounts
  useEffect(() => {
    return () => {
      clearSelection();
    };
  }, [clearSelection]);

  // Filter items by selected date (if any) - MUST BE BEFORE CONDITIONAL RETURNS
  const filteredItems = useMemo(() => {
    if (!data || !data.items) return [];
    if (!selectedDate) return data.items;

    // Use LOCAL date comparison (not UTC) to match timeline grouping
    const selectedYear = selectedDate.getFullYear();
    const selectedMonth = selectedDate.getMonth();
    const selectedDay = selectedDate.getDate();

    return data.items.filter((item) => {
      const itemDate = new Date(item.playedAt);
      return (
        itemDate.getFullYear() === selectedYear &&
        itemDate.getMonth() === selectedMonth &&
        itemDate.getDate() === selectedDay
      );
    });
  }, [data, selectedDate]);

  // Loading state
  if (isLoading && currentPage.items.length === 0) {
    return (
      <div className="flex flex-col h-full bg-zinc-950">
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3 mb-2">
            <Music className="w-6 h-6 text-emerald-500" />
            <h2 className="text-xl font-semibold text-zinc-100">Listening History</h2>
          </div>
          <p className="text-sm text-zinc-500">Loading your recent tracks...</p>
        </div>
        <div className="flex-1 overflow-auto p-4">
          <Skeleton variant="track" count={10} />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col h-full bg-zinc-950">
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3 mb-2">
            <Music className="w-6 h-6 text-emerald-500" />
            <h2 className="text-xl font-semibold text-zinc-100">Listening History</h2>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <AlertTriangle className="w-16 h-16 text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-zinc-300 mb-2">
            Failed to Load History
          </h3>
          <p className="text-sm text-zinc-500 max-w-md mb-6">
            {error instanceof Error ? error.message : 'An unexpected error occurred while loading your listening history.'}
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.items.length === 0) {
    return (
      <div className="flex flex-col h-full bg-zinc-950">
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3 mb-2">
            <Music className="w-6 h-6 text-emerald-500" />
            <h2 className="text-xl font-semibold text-zinc-100">Listening History</h2>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <Music className="w-16 h-16 text-zinc-700 mb-4" />
          <h3 className="text-lg font-medium text-zinc-400 mb-2">
            No Listening History Yet
          </h3>
          <p className="text-sm text-zinc-500 max-w-md">
            Start listening to music on Spotify and your recent tracks will appear here.
            You can select tracks and copy them to your playlists in the Workbench.
          </p>
        </div>
      </div>
    );
  }

  // filteredItems is already computed above (before conditional returns)

  // Convert PlayHistory items to TrackWithInstanceId for TrackTable
  const tracksWithInstanceIds = filteredItems.map((item, index) => ({
    ...item.track,
    instanceId: `history-${item.playedAt}-${index}`, // Unique instance ID for each play
  }));

  // Create playedAtMap for timestamp display in history mode
  const playedAtMap = new Map(
    filteredItems.map((item, index) => [
      `history-${item.playedAt}-${index}`,
      item.playedAt,
    ])
  );

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      {/* Header with pagination and controls */}
      <HistoryHeader
        items={data.items}
        cursors={data.cursors}
        onRefresh={refetch}
        isRefreshing={isLoading}
      />

      {/* Timeline visualization */}
      <HistoryTimeline
        items={data.items}
        onDateSelect={setSelectedDate}
        selectedDate={selectedDate}
      />

      {/* Track table */}
      <div className="flex-1 overflow-auto">
        <TrackTable
          mode="history"
          tracks={tracksWithInstanceIds}
          audioFeaturesMap={new Map()} // History mode doesn't need audio features
          playedAtMap={playedAtMap}
        />
      </div>

      {/* Toolbar with selection and copy actions */}
      <HistoryToolbar />
    </div>
  );
}
